import {LoadingSection} from '#/atoms/loading-section';
import {ReactComponent as IcoChart} from '#/components/icons/ico-chart.svg';
import {LandMarketIndicator} from '#/components/indicators/market-indicator';
import {TransLandCityStateText} from '#/components/land/land-city-state-text';
import {LandThumbnail} from '#/components/lands/land-thumbnail';
import {useLandRentalYield} from '#/hooks/query/use-land-rental-yield';
import {CoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {ArrowTrendingUpIcon, CurrencyDollarIcon} from '@heroicons/react/24/solid';
import {FunctionComponent, ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {Land, LandMarketInfo, getLandPremium} from 'shareland-common';

type LandTileProps = {
  className?: string;
  onLand: (land: Land, landMarketInfo: LandMarketInfo | undefined) => void;
  selected: boolean;
  land: Land;
  landMarketInfo: LandMarketInfo | undefined;
  displayPref: CoinDisplayPreferences;
  bottomInformationVariant?: 'volume' | 'premium' | 'rentalYield';
};
export const LandTile: FunctionComponent<LandTileProps> = ({
  onLand,
  land,
  landMarketInfo,
  className,
  displayPref,
  selected,
  bottomInformationVariant = 'volume',
}) => {
  const {t} = useTranslation('translation');

  const onClick = () => {
    if (!!land) {
      onLand(land, landMarketInfo);
    }
  };

  const loadingAnimationClassName = !land ? 'animate-pulse bg-gray-300' : '';
  const selectedClassName = selected ? 'ring-1 ring-deepblue border-deepblue' : 'border-gray-100';
  const buttonClassName = `m-0 relative w-full h-full group rounded-2xl border overflow-hidden-clip-path ${loadingAnimationClassName} ${selectedClassName} ${
    className ?? ''
  } `;

  return (
    <button onClick={onClick} className={buttonClassName}>
      <LandThumbnail
        land={land}
        className={'w-full h-full object-cover group-hover:scale-110 transition-transform duration-500'}
      />

      <div className='absolute bottom-0 left-0 w-full bg-gradient-to-t from-black to-transparent px-5 pb-4 pt-8'>
        <div className='text-white font-bold text-left'>
          {t('components.locationGrid.tile.title', {
            landName: land.name,
          })}
        </div>
        <div className='text-gray-100 font-semibold text-sm text-left'>
          <TransLandCityStateText land={land} />
        </div>
        <div className='w-full bg-white h-[1px] opacity-25 mt-2 mb-1'></div>

        <LandTileBottomInformation
          land={land}
          landMarketInfo={landMarketInfo}
          displayPref={displayPref}
          bottomInformationVariant={bottomInformationVariant}
        />
      </div>

      <div
        className={`absolute top-3 right-5 mt-2 rounded-full ${
          landMarketInfo
            ? 'flex items-center py-1 px-2 bg-black text-xs text-white font-bold'
            : 'animate-pulse bg-gray-800 animation-delay-300 w-[150px] h-[24px]'
        }`}
      >
        {landMarketInfo && (
          <LandMarketIndicator
            className='text-white font-bold text-xs'
            land={land}
            range='daily'
            landMarketInfo={landMarketInfo}
            showDifference={false}
            iconSize='xs'
          />
        )}
      </div>
    </button>
  );
};

const LandTileBottomInformation: FunctionComponent<
  Pick<LandTileProps, 'land' | 'landMarketInfo' | 'displayPref' | 'bottomInformationVariant'>
> = ({land, landMarketInfo, displayPref, bottomInformationVariant}) => {
  const {t} = useTranslation('translation');

  if (bottomInformationVariant === 'volume') {
    return (
      <BottomInformationContent>
        {{
          icon: <IcoChart />,
          text: t('components.locationGrid.tile.infoFooter.volume', {
            currencySymbol: displayPref.symbol,
            amount: displayPref.displayAmount(land.syntheticMarketData.dailyVolume),
          }),
        }}
      </BottomInformationContent>
    );
  }

  if (bottomInformationVariant === 'premium') {
    return (
      <BottomInformationContent>
        {{
          icon: <CurrencyDollarIcon className='w-4 h-4 fill-white stroke-transparent' />,
          text: landMarketInfo ? (
            t('components.locationGrid.tile.infoFooter.premium', {
              value: getLandPremium(landMarketInfo?.marketPrice, landMarketInfo?.oraclePrice),
            })
          ) : (
            <>&nbsp;</>
          ),
        }}
      </BottomInformationContent>
    );
  }

  if (bottomInformationVariant === 'rentalYield') {
    return (
      <BottomInformationContent>
        {{
          icon: <ArrowTrendingUpIcon className='w-4 h-4 fill-white stroke-transparent' />,
          text: <RentalYieldValue land={land} />,
        }}
      </BottomInformationContent>
    );
  }

  return null;
};

const RentalYieldValue: FunctionComponent<{land: Land}> = ({land}) => {
  const {data, isLoading, error} = useLandRentalYield(land.tokenSymbol);
  const {t} = useTranslation('translation');

  if (isLoading) {
    return (
      <div className='h-5 py-1'>
        <LoadingSection w='w-14' h='h-3' animate />
      </div>
    );
  }

  if (error) {
    <span className='text-downred italic'>{t('pages.actionMenu.components.contentSection.infoRow.errorLoading')}</span>;
  }

  if (data === undefined || data === null) {
    return <span>{t('pages.actionMenu.components.contentSection.infoRow.empty')}</span>;
  }

  return t('components.locationGrid.tile.infoFooter.rentalYield', {value: data});
};

const BottomInformationContent: FunctionComponent<{
  children: {
    icon: ReactNode;
    text: ReactNode;
  };
}> = ({children}) => (
  <div className='flex items-center'>
    {children.icon}
    <div className='ml-2 text-white font-light text-left'>{children.text}</div>
  </div>
);
